import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";

const BeamContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
  overflow: hidden;
`;

const BeamSVG = styled.svg`
  width: 100%;
  height: 100%;
  opacity: 0;
  animation: fadeIn 1s ease-in forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const LightBeam = ({ orangeCubePosition, targetRef, containerRef }) => {
  const [beamPath, setBeamPath] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [adjustedCubePosition, setAdjustedCubePosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Si déjà initialisé, ne rien faire (évite les recalculs sur mobile)
    if (isInitializedRef.current) {
      return;
    }

    if (!orangeCubePosition || !targetRef?.current || !containerRef?.current) {
      setIsVisible(false);
      return;
    }

    const updateBeam = () => {
      if (!targetRef?.current || !containerRef?.current) return;

      // Obtenir la position du conteneur (Section1)
      const containerRect = containerRef.current.getBoundingClientRect();

      const targetRect = targetRef.current.getBoundingClientRect();
      const targetX = targetRect.left + targetRect.width / 2 - containerRect.left;
      const targetY = targetRect.top + targetRect.height / 2 - containerRect.top;

      const { x: startX, y: startY } = orangeCubePosition;

      // Ajuster les coordonnées du cube par rapport au conteneur
      const adjustedStartX = startX - containerRect.left;
      const adjustedStartY = startY - containerRect.top;

      // Sauvegarder la position ajustée pour le cercle
      setAdjustedCubePosition({ x: adjustedStartX, y: adjustedStartY });

      // Créer un path avec une courbe douce (Bézier quadratique)
      const controlX = (adjustedStartX + targetX) / 2;
      const controlY = Math.min(adjustedStartY, targetY) - 100; // Courbe vers le haut

      const path = `M ${adjustedStartX} ${adjustedStartY} Q ${controlX} ${controlY} ${targetX} ${targetY}`;
      setBeamPath(path);
      setIsVisible(true);

      // Marquer comme initialisé pour éviter les recalculs
      isInitializedRef.current = true;
    };

    updateBeam();

    // Plus besoin de resize listener - la position est figée après le premier calcul
  }, [orangeCubePosition, targetRef, containerRef]);

  if (!isVisible) return null;

  return (
    <BeamContainer>
      <BeamSVG>
        <defs>
          {/* Gradient orange pour le rayon */}
          <linearGradient id="beamGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255, 165, 0, 0.8)" />
            <stop offset="50%" stopColor="rgba(255, 140, 0, 0.6)" />
            <stop offset="100%" stopColor="rgba(255, 165, 0, 0.8)" />
          </linearGradient>

          {/* Filtre de glow */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Rayon lumineux principal */}
        <path
          d={beamPath}
          fill="none"
          stroke="rgba(255, 165, 0, 0.9)"
          strokeWidth={isMobile ? "3" : "6"}
          strokeLinecap="round"
          filter="url(#glow)"
        >
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="2s"
            repeatCount="indefinite"
          />
        </path>

        {/* Rayon secondaire pour profondeur */}
        <path
          d={beamPath}
          fill="none"
          stroke="rgba(255, 200, 100, 0.4)"
          strokeWidth={isMobile ? "6" : "12"}
          strokeLinecap="round"
        >
          <animate
            attributeName="opacity"
            values="0.4;0.7;0.4"
            dur="2s"
            begin="0.5s"
            repeatCount="indefinite"
          />
        </path>

        {/* Point lumineux sur le cube */}
        <circle
          cx={adjustedCubePosition.x}
          cy={adjustedCubePosition.y}
          r={isMobile ? "5" : "8"}
          fill="rgba(255, 165, 0, 1)"
          filter="url(#glow)"
        >
          <animate
            attributeName="r"
            values="6;10;6"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>
      </BeamSVG>
    </BeamContainer>
  );
};

export default LightBeam;
